import React, { useContext, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import { ChatContext } from "../context/ChatContext";
import CreateGroup from "./CreateGroup";
import toast from "react-hot-toast";
const Search = () => {
  const [username, setUsername] = useState("");
  const [user, setUser] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  const { currentUser, userEventCode, loading } = useContext(AuthContext);
  const { dispatch } = useContext(ChatContext);

  const handleSearch = async () => {
    if (!username.trim()) {
      toast.error("Please enter a username to search");
      return;
    }
    
    console.log("Search initiated - userEventCode:", userEventCode, "loading:", loading);
    console.log("currentUser:", currentUser);
    
    if (loading) {
      toast.error("Please wait while we load your profile...");
      return;
    }
    
    // Try to get eventCode from userEventCode or currentUser.eventCode as fallback
    const eventCode = userEventCode || currentUser?.eventCode;
    
    if (!eventCode) {
      console.log("No eventCode found. currentUser:", currentUser);
      toast.error("Event code not found. Please refresh the page or login again.");
      return;
    }

    console.log("Using eventCode:", eventCode);

    const q = query(
      collection(db, "users"),
      where("displayName", "==", username)
    );

    try {
      const querySnapshot = await getDocs(q);
      let foundUser = null;
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        console.log("Found user document:", userData);
        
        // Check if user is in the same event
        const userEvents = userData.events || [userData.eventCode].filter(Boolean);
        console.log("User events:", userEvents, "Looking for:", eventCode);
        
        if (userEvents.includes(eventCode)) {
          // Ensure the user object has all required fields
          foundUser = {
            uid: userData.uid || doc.id,
            displayName: userData.displayName || userData.username || "Unknown User",
            photoURL: userData.photoURL || "https://via.placeholder.com/50/6c757d/ffffff?text=U",
            email: userData.email,
            ...userData // Include all other fields
          };
          console.log("Selected user for chat:", foundUser);
        }
      });
      
      if (foundUser && foundUser.uid !== currentUser.uid) {
        setUser(foundUser);
        toast.success(`Found user: ${foundUser.displayName}`);
      } else if (foundUser && foundUser.uid === currentUser.uid) {
        setUser(null);
        toast.error("You cannot chat with yourself");
      } else {
        setUser(null);
        toast.error("User not found in this event");
      }
    } catch (err) {
      console.error("Search error:", err);
      toast.error("Error searching for user. Please try again.");
    }
  };

  const handleKey = (e) => {
    e.code === "Enter" && handleSearch();
  };

  const handleSelect = async () => {
    if (!user) return;
    
    // Get eventCode with fallback
    const eventCode = userEventCode || currentUser?.eventCode;
    
    if (!eventCode) {
      toast.error("Event code not found. Please refresh the page.");
      return;
    }
    
    //check whether the group(chats in firestore) exists, if not create
    const combinedId =
      currentUser.uid > user.uid
        ? currentUser.uid + user.uid
        : user.uid + currentUser.uid;
    
    // Use event-specific user chats - define these outside the if/else blocks
    const currentUserChatsId = `${currentUser.uid}_${eventCode}`;
    const otherUserChatsId = `${user.uid}_${eventCode}`;
    
    try {
      toast.loading("Setting up chat...");
      
      console.log("Creating/checking chat between:", currentUser.uid, "and", user.uid);
      console.log("Combined chat ID:", combinedId);
      console.log("Event code:", eventCode);
      console.log("UserChats IDs:", currentUserChatsId, otherUserChatsId);
      
      const res = await getDoc(doc(db, "chats", combinedId));

      if (!res.exists()) {
        console.log("Chat doesn't exist, creating new chat");
        //create a chat in chats collection
        await setDoc(doc(db, "chats", combinedId), { 
          messages: [],
          eventCode: eventCode
        });

        console.log("Creating userChats entries:", currentUserChatsId, otherUserChatsId);

        //create user chats
        try {
          await updateDoc(doc(db, "userChats", currentUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: user.uid,
              displayName: user.displayName,
              photoURL: user.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        } catch (updateError) {
          console.log("Creating new currentUser userChats document");
          // Document might not exist, create it
          await setDoc(doc(db, "userChats", currentUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: user.uid,
              displayName: user.displayName,
              photoURL: user.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        }

        try {
          await updateDoc(doc(db, "userChats", otherUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              photoURL: currentUser.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        } catch (updateError) {
          // Document might not exist, create it
          await setDoc(doc(db, "userChats", otherUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              photoURL: currentUser.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        }
      } else {
        console.log("Chat already exists:", res.data());
        console.log("Updating userChats for existing chat with current event:", eventCode);
        
        // Even if chat exists, we need to update userChats for the current event
        try {
          await updateDoc(doc(db, "userChats", currentUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: user.uid,
              displayName: user.displayName,
              photoURL: user.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        } catch (updateError) {
          console.log("Creating new currentUser userChats document for existing chat");
          await setDoc(doc(db, "userChats", currentUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: user.uid,
              displayName: user.displayName,
              photoURL: user.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        }

        try {
          await updateDoc(doc(db, "userChats", otherUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              photoURL: currentUser.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        } catch (updateError) {
          console.log("Creating new otherUser userChats document for existing chat");
          await setDoc(doc(db, "userChats", otherUserChatsId), {
            [combinedId + ".userInfo"]: {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              photoURL: currentUser.photoURL,
            },
            [combinedId + ".eventCode"]: eventCode,
            [combinedId + ".date"]: serverTimestamp(),
          });
        }
      }
      
      // Dispatch to ChatContext to actually open the chat
      dispatch({ type: "CHANGE_USER", payload: user });
      
      toast.dismiss();
      toast.success(`Chat with ${user.displayName} is ready!`);
      
      // Clear the search after successful chat creation
      setUser(null);
      setUsername("");
    } catch (err) {
      console.error("Chat creation error:", err);
      toast.error("Error creating chat. Please try again.");
      // Don't clear user state on error so they can try again
    }
  };
  return (
    <div className="search">
      <div className="searchForm">
        <input
          type="text"
          placeholder="Find a user"
          onKeyDown={handleKey}
          onChange={(e) => setUsername(e.target.value)}
          value={username}
          disabled={loading || (!userEventCode && !currentUser?.eventCode)}
        />
      </div>
      {loading && <p style={{fontSize: '12px', color: '#666'}}>Loading your profile...</p>}
      {!loading && !userEventCode && !currentUser?.eventCode && <p style={{fontSize: '12px', color: '#ff4444'}}>Event code not loaded. Please refresh.</p>}
      {!loading && (userEventCode || currentUser?.eventCode) && <p style={{fontSize: '12px', color: '#00aa00'}}>Event: {userEventCode || currentUser?.eventCode}</p>}
      <button 
        className="createGroupBtn"
        onClick={() => setShowCreateGroup(true)}
        disabled={loading || (!userEventCode && !currentUser?.eventCode)}
      >
        Create Group Chat
      </button>
      {user && (
        <div className="userChat" onClick={handleSelect}>
          <img src={user.photoURL} alt="" />
          <div className="userChatInfo">
            <span>{user.displayName}</span>
          </div>
        </div>
      )}
      {showCreateGroup && (
        <CreateGroup onClose={() => setShowCreateGroup(false)} />
      )}
    </div>
  );
};

export default Search;
